package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.AccentSoft
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.WebIconSize
import com.merkost.handpass.sharedweb.theme.WebMotion
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.disabled as disabledSelector
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement

val WebPaginationCellStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .minWidth(TouchTarget.value())
            .height(TouchTarget.value())
            .borderRadius(RadiusControl.value())
            .border(WebSize.P1.value(), LineStyle.Solid, Transparent.value())
            .backgroundColor(Transparent.value())
            .cursor(Cursor.Pointer)
            .fontWeight(500)
            .color(OnSurface.value())
            .transitionSurfaceAction()
            .styleModifier {
                property("font-size", "${HandpassTypography.Caption}")
                property("font-family", "inherit")
                property("font-variant-numeric", "tabular-nums")
            }
    }
    hover {
        Modifier.backgroundColor(SurfaceMuted.value())
    }
    active {
        Modifier.scale(WebMotion.PressScale)
    }
    focusVisible {
        Modifier
            .outline(WebSize.P0.value(), LineStyle.None, Transparent.value())
            .boxShadow(spreadRadius = WebSize.P3.value(), color = AccentRing.value())
    }
    disabledSelector {
        Modifier
            .opacity(0.4)
            .cursor(Cursor.NotAllowed)
    }
}

val WebPaginationActiveCellStyle = WebPaginationCellStyle.extendedBy {
    base {
        Modifier
            .backgroundColor(AccentSoft.value())
            .color(Accent.value())
            .fontWeight(600)
    }
    hover {
        Modifier.backgroundColor(AccentSoft.value())
    }
}

val WebPaginationNavButtonStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .height(TouchTarget.value())
            .minWidth(TouchTarget.value())
            .borderRadius(RadiusControl.value())
            .border(WebSize.P1.value(), LineStyle.Solid, Transparent.value())
            .backgroundColor(Transparent.value())
            .cursor(Cursor.Pointer)
            .color(OnSurfaceMuted.value())
            .transitionSurfaceAction()
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurface.value())
    }
    active {
        Modifier.scale(WebMotion.PressScale)
    }
    focusVisible {
        Modifier
            .outline(WebSize.P0.value(), LineStyle.None, Transparent.value())
            .boxShadow(spreadRadius = WebSize.P3.value(), color = AccentRing.value())
    }
    disabledSelector {
        Modifier
            .opacity(0.4)
            .cursor(Cursor.NotAllowed)
    }
}

@Composable
fun WebPagination(
    currentPage: Int,
    totalPages: Int,
    totalCount: Long? = null,
    onPageChange: (Int) -> Unit,
) {
    if (totalPages <= 1 && totalCount == null) return

    Div(
        attrs = Modifier
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
            .gap(HandpassSpacing.SM)
            .styleModifier {
                property("margin-top", "${HandpassSpacing.XL}")
                property("flex-wrap", "wrap")
            }
            .toAttrs()
    ) {
        Span(
            attrs = Modifier
                .color(OnSurfaceMuted.value())
                .styleModifier {
                    property("font-size", "${HandpassTypography.Caption}")
                    property("font-variant-numeric", "tabular-nums")
                }
                .toAttrs()
        ) {
            Text(if (totalCount != null) "$totalCount results" else "")
        }

        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .gap(HandpassSpacing.XS)
                .styleModifier {
                    property("max-width", "100%")
                    property("overflow-x", "auto")
                }
                .toAttrs()
        ) {
            Button(
                attrs = WebPaginationNavButtonStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                    attr("type", "button")
                    attr("aria-label", "Previous page")
                    if (currentPage <= 1) disabled()
                    onClick { if (currentPage > 1) onPageChange(currentPage - 1) }
                }
            ) {
                Icon(WebIcon.ChevronLeft, size = WebIconSize.Sm)
            }

            val windowSize = 3
            val half = windowSize / 2
            val startPage = maxOf(1, minOf(currentPage - half, totalPages - windowSize + 1))
            val endPage = minOf(totalPages, startPage + windowSize - 1)

            for (page in startPage..endPage) {
                val isActive = page == currentPage
                val cellStyle = if (isActive) WebPaginationActiveCellStyle else WebPaginationCellStyle
                Button(
                    attrs = cellStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                        attr("type", "button")
                        if (isActive) attr("aria-current", "page")
                        onClick { onPageChange(page) }
                    }
                ) {
                    Text(page.toString())
                }
            }

            Button(
                attrs = WebPaginationNavButtonStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                    attr("type", "button")
                    attr("aria-label", "Next page")
                    if (currentPage >= totalPages) disabled()
                    onClick { if (currentPage < totalPages) onPageChange(currentPage + 1) }
                }
            ) {
                Icon(WebIcon.ChevronRight, size = WebIconSize.Sm)
            }
        }
    }
}
